'use client'

import {
  ConversationConfig,
  ConversationError,
  ConversationManager,
  ConversationState,
  ImageChangeEvent,
  SubtitleChangeEvent,
  WordHighlightEvent,
} from '@/ug-core'
import { ScreenWakeLock } from '@/ug-core/utils/screenWakeLock'
import { AvatarAssets } from './components/AnimatedSpineCharacter'
import React, { useEffect, useRef, useState } from 'react'
import { PlayButton } from './components/PlayButton/PlayButton'
import { apiInstance } from '@/apiInstance'
import { SubtitlesDisplay } from './components/SubtitlesDisplay'
import AnimatedSpineCharacter, {
  AnimatedSpineCharacterHandle,
} from './components/AnimatedSpineCharacter'
import toast from 'react-hot-toast'
import { useIsMobile } from '@/hooks/useIsMobile'
import { ImageFrame } from './components/ImageFrame'
import { ChatInputBox } from './components/ChatInputBox/ChatInputBox'
import { Mic, MicOff } from 'lucide-react'

import { StringMessage } from '@/ug-core/types';

interface ChatAvatarProps {
  componentId: string
  avatarAssets: AvatarAssets
  personaId?: string
  contextValues?: Record<string, string | number | boolean>
  imageFrame?: string
  isEnabledSubtitles?: boolean
  onStringMessage?: (message: StringMessage) => void;
}

export const ChatAvatar: React.FC<ChatAvatarProps> = ({
  componentId,
  personaId,
  avatarAssets,
  contextValues,
  imageFrame,
  isEnabledSubtitles = true,
  onStringMessage,
}) => {
  const [state, setState] = useState<ConversationState>()
  const [subtitles, setSubtitles] = useState<any>(null)
  const [hasExperienceStarted, setHasExperienceStarted] = useState(false)
  const [isTextOnly, setIsTextOnly] = useState(false)

  const isPaused = state === 'uninitialized' || state === 'idle'
  const isAudioPlaying = state === 'playing'
  const isUserSpeaking = state === 'userSpeaking'
  const isPlaybackPaused = state === 'paused'
  const isMobile = useIsMobile()

  const animatedCharacterRef = useRef<AnimatedSpineCharacterHandle>(null)
  const conversationManagerRef = useRef<ConversationManager | null>(null)
  const screenWakeLockRef = useRef(new ScreenWakeLock())
  const [currentImage, setCurrentImage] = useState<string | null>(null)
  const [textInput, setTextInput] = useState('')
  const [isNetworkReady, setIsNetworkReady] = useState(false)
  const [viewportHeight, setViewportHeight] = useState<number | null>(null)

  useEffect(() => {
    if (contextValues) {
      conversationManagerRef.current?.updateContextValues(contextValues);
    }
  }, [contextValues]);

  useEffect(() => {
    const updateHeight = () => {
      if (isMobile) {
        setViewportHeight(window.innerHeight)
      }
    }

    updateHeight()
    window.addEventListener('resize', updateHeight)

    const wakeLock = screenWakeLockRef.current
    return () => {
      window.removeEventListener('resize', updateHeight)
      wakeLock.release()
    }
  }, [isMobile])

  const [isTransitioning, setIsTransitioning] = useState(false)

  const handlePlayButtonClick = () => {
    if (!hasExperienceStarted) {
      screenWakeLockRef.current?.request()
      if (imageFrame === 'fullscreen') {
        setIsTransitioning(true)
        setTimeout(() => {
          setHasExperienceStarted(true)
          setIsTransitioning(false)
          conversationManagerRef.current?.initialize()
        }, 500)
      } else {
        setHasExperienceStarted(true)
        conversationManagerRef.current?.initialize()
      }
      return
    }

    if (isAudioPlaying) {
      conversationManagerRef.current?.pause()
      screenWakeLockRef.current?.release()
    } else if (isPlaybackPaused) {
      conversationManagerRef.current?.resume()
      screenWakeLockRef.current?.request()
    } else if (isUserSpeaking) {
      conversationManagerRef.current?.forceInputComplete()
    }
  }

  const handleSendText = () => {
    if (textInput.trim()) {
      conversationManagerRef.current?.sendText(textInput)
      setTextInput('')
    }
  }

  const handleToggleTextOnly = async () => {
    const newIsTextOnly = !isTextOnly
    setIsTextOnly(newIsTextOnly)
    await conversationManagerRef.current?.toggleTextOnlyInput(newIsTextOnly)
    toast.success(newIsTextOnly ? 'Text-only mode activated 💬' : 'Microphone mode activated 🎙️')
  }

  if (!conversationManagerRef.current) {
    const wsUrl = apiInstance.apiUrl.replace(/^http/, 'ws')
    const serverUrl = `${wsUrl}/components/${componentId}/interact-ws`
    const convConfig: ConversationConfig = {
      serverUrl,
      componentId,
      personaId,
      imageFrame,
      contextValues: {
        ...contextValues,
      },
      authToken: apiInstance.authToken,
      inputCapabilities: {
        audio: !isTextOnly,
        text: isTextOnly,
      },
      hooks: {
        onStateChange: (state: ConversationState) => {
          setState(state)
        },
        onNetworkStatusChange: (isReady: boolean) => {
          setIsNetworkReady(isReady)
        },
        onSubtitleHighlight: (event: WordHighlightEvent) => {
          setSubtitles(event)
        },
        onSubtitleChange: (event: SubtitleChangeEvent) => {
          if (event.currentLine || event.nextLine) {
            setSubtitles({
              globalWordIndex: -1,
              currentLineIndex: 0,
              wordIndexInLine: -1,
              currentLineData: event.currentLine,
              nextLineData: event.nextLine,
            })
          } else {
            setSubtitles(null)
          }
        },
        onImageChange(event: ImageChangeEvent) {
          const imageData = `data:${event.format};base64,${event.image}`
          setCurrentImage(imageData)
        },
        onError: (error: ConversationError) => {
          toast.error(typeof error === 'string' ? error : error?.message || 'Unknown error')
        },
        onStringMessage: onStringMessage,
        onAvatarAnimationChanged: ({
          name,
          layer,
          loop,
        }: {
          name: string
          layer: number
          loop: boolean
        }) => {
          animatedCharacterRef.current?.setAnimation(layer, name, loop)
        },
      },
    }
    conversationManagerRef.current = new ConversationManager(convConfig)
  }

  return (
    <div
      className={`flex flex-col items-center justify-center w-full max-w-full h-full relative ${
        isMobile ? 'px-0' : ''
      }`}
      style={{
        ...(isMobile ? { padding: 0, margin: 0 } : {}),
        height: viewportHeight ? `${viewportHeight}px` : '100vh', //Fixed issues with mobile height
      }}
    >
      <div
        className="relative w-full flex-grow"
        style={{
          zIndex: 50,
        }}
      >
        <AnimatedSpineCharacter
          ref={animatedCharacterRef}
          avatarAssets={avatarAssets}
          applyFilter={!hasExperienceStarted}
          zIndex={imageFrame === 'fullscreen' ? 51 : undefined}
          isDraggable={imageFrame === 'fullscreen'}
          height={imageFrame === 'fullscreen' ? (isMobile ? (hasExperienceStarted ? '30vh' : '65vh') : '53%') : '59vh'}
          width={imageFrame === 'fullscreen' ? (isMobile ? (hasExperienceStarted ? '40vw' : '65vw') : '44vw') : '100%'}
          className={`transition-opacity duration-500 ease-in-out ${
            imageFrame === 'fullscreen'
              ? isTransitioning
                ? 'opacity-0 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
                : hasExperienceStarted
                ? 'opacity-100 fixed bottom-4 left-0'
                : 'opacity-100 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
              : 'absolute'
          }`}
        />
        <div className="absolute top-10 left-4 z-[60]">
          <button
            onClick={handleToggleTextOnly}
            className="p-2 bg-gray-800 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={state === 'initializing'}
          >
            {isTextOnly ? <MicOff size={24} /> : <Mic size={24} />}
          </button>
        </div>
        {imageFrame && <ImageFrame currentImage={currentImage} mode={imageFrame} />}
        {/* Play button - positioned over bot only before experience starts */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-all duration-150 ${
            !hasExperienceStarted ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          style={{
            display: hasExperienceStarted ? 'none' : 'flex',
            zIndex: 60,
          }}
        >
          <PlayButton
            isPaused={isPaused}
            isAudioPlaying={isAudioPlaying}
            isUserSpeaking={isUserSpeaking}
            onClick={handlePlayButtonClick}
            showTapToPlay={true}
            hasExperienceStarted={hasExperienceStarted}
            isPlaybackPaused={isPlaybackPaused}
          />
        </div>
      </div>
      <div
        className={`relative w-full flex flex-col items-center justify-end px-4 z-[60] pb-4 ${
          isMobile ? 'fixed bottom-0 left-0 right-0': ''
        }`}
      >
        {isEnabledSubtitles && (
          <div className="w-full max-w-lg">
            <SubtitlesDisplay subtitles={subtitles} isPaused={isPlaybackPaused} />
          </div>
        )}
        {isTextOnly ? (
          <div className="w-full max-w-lg mt-4">
            <ChatInputBox
              textInput={textInput}
              setTextInput={setTextInput}
              onSend={handleSendText}
              isEnabled={!isAudioPlaying && isNetworkReady}
              isAudioPlaying={isAudioPlaying}
              isPlaybackPaused={isPlaybackPaused}
              onPause={() => conversationManagerRef.current?.pause()}
              onResume={() => conversationManagerRef.current?.resume()}
            />
          </div>
        ) : (
          <div
            className={`flex flex-col items-center transition-all duration-150 mt-4 ${
              hasExperienceStarted ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            style={{
              zIndex: 10,
            }}
          >
            <PlayButton
              isPaused={isPaused}
              isAudioPlaying={isAudioPlaying}
              isUserSpeaking={isUserSpeaking}
              onClick={handlePlayButtonClick}
              showTapToPlay={false}
              hasExperienceStarted={hasExperienceStarted}
              isPlaybackPaused={isPlaybackPaused}
              className="w-20 h-20"
            />
          </div>
        )}
      </div>
      {!isEnabledSubtitles && !isTextOnly && <div className="h-6" />}
      {isMobile && <div className="h-8" />}
    </div>
  )
}

